/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@skyxops/ui';
import { ChevronDown, LogOut, Settings, ShieldCheck } from 'lucide-react';
import type { TilesResponse } from '../api';

function initials(s: string): string {
  const parts = s.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || s[0]?.toUpperCase() || '?';
}

/** Humanize any underscore_case label into a friendly one — generic, so it
 *  works for every current and future value (no per-value hardcoding). Short
 *  tokens (<=3 chars, e.g. "msp") render as acronyms: `msp_user` -> "MSP User",
 *  `client_user` -> "Client User", `admin` -> "Admin". */
export function humanizeLabel(t: string): string {
  return t
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

/** Top-bar account dropdown — the entry point to /profile. */
export function AccountMenu({
  principal, onLogout,
}: { principal: TilesResponse['principal'] | null; onLogout: () => void }) {
  const navigate = useNavigate();
  const name = principal?.display_name ?? principal?.user_id ?? 'Account';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg px-2 py-1 text-white/90 outline-none hover:bg-white/10">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-white/15 text-xs font-semibold">{initials(name)}</span>
        <span className="hidden text-sm sm:block">{name}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span>{name}</span>
          {principal?.user_principal_type && (
            <span className="text-xs font-normal text-muted-foreground">{humanizeLabel(principal.user_principal_type)}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/profile')}>
          <Settings className="mr-2 h-4 w-4" /> Account settings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/profile#security')}>
          <ShieldCheck className="mr-2 h-4 w-4" /> Security
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className="text-red-600 focus:text-red-600 dark:text-red-400">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
